import type { RenderTask } from '../../widgets/reel-create-form/lib/buildRenderTasks';
import { backendTokenSync } from '../lib/backendAuth';
import { sendPublishTelemetry } from '../lib/telemetry';
import { backendFetch } from './client';

export type ReelJobEvent =
  | { phase: 'job'; jobId: string }
  | { phase: 'upload'; percent: number }
  | { phase: 'render'; percent: number; taskIdx?: number; totalTasks?: number }
  | { phase: 'publish'; percent?: number; accountId?: string; status?: 'success' | 'error' | 'retrying'; attempt?: number; error?: string; stage?: string }
  | { phase: 'done'; results?: { accountId: string; status: 'success' | 'error'; instagramMediaId?: string; error?: string; story?: { status: 'success' | 'error'; mediaId?: string; error?: string } }[] }
  // Рендер закончен, публикация поставлена в очередь на указанное время.
  | { phase: 'scheduled'; id: string; runAt: string; accounts: number }
  | { phase: 'error'; error: string };

export interface SubmitReelJobInput {
  file: File;
  meta: {
    batchId: string;
    jobId: string;
    // igUsername = IG handle, used by the phone-publish backend to resolve which
    // phone holds the account (via the device inventory). Optional: the Graph-API
    // path ignores it.
    accounts: { id: string; name?: string; igUsername?: string }[];
    captions: Record<string, string>;
    renderTasks: RenderTask[];
    duplicateToStory?: boolean;
    collaborators?: string[];
    // Optional replacement audio track for the whole reel.
    music?: { url: string; volume: number; startSec: number };
    // When true the uploaded `file` is a still photo: the backend loops it into
    // a video at native aspect and derives length from the music track.
    stillImage?: boolean;
    publishMode?: 'phone' | 'api';
    // Отложенная публикация (ISO). Видео рендерится сразу, а публикация уходит
    // в очередь на бэкенде и выполняется в назначенное время.
    scheduledAt?: string;
    // Тип контента — бэкенд кладёт его в журнал страницы «Публикации».
    category?: string;
    // Размер файла — бэкенд по нему (с аккаунтами и подписями) узнаёт дубль.
    fileSize?: number;
  };
  onEvent?: (event: ReelJobEvent) => void;
  // Raw upload progress (0-100) from the request body bytes. The SSE stream's
  // `upload` event only fires once at 100% (after the server has the whole file),
  // so this is the only source of granular upload progress.
  onUploadProgress?: (percent: number) => void;
  onJobId?: (jobId: string) => void;
  signal?: AbortSignal;
}

// Photo POST job: multipart (meta + N images) → /api/posts, consuming the same
// SSE event shape as reels. Simpler than the reel uploader (no XHR upload-progress
// / polling fallback) since images are small.
export interface SubmitPostJobInput {
  images: File[];
  meta: {
    batchId: string;
    jobId: string;
    accounts: { id: string; igUsername?: string }[];
    captions: Record<string, string>;
  };
  signal?: AbortSignal;
  onEvent?: (event: ReelJobEvent) => void;
}

export async function submitPostJobServer(input: SubmitPostJobInput): Promise<ReelJobEvent[]> {
  const form = new FormData();
  form.append('meta', JSON.stringify(input.meta));
  for (const img of input.images) form.append('media', img, img.name);

  const res = await backendFetch('/api/posts', {
    method: 'POST', body: form, headers: { Accept: 'text/event-stream' }, signal: input.signal,
  });
  if (!res.ok || !res.body) {
    throw new Error(`/api/posts failed: HTTP ${res.status}`);
  }
  const collected: ReelJobEvent[] = [];
  // Resolve as soon as a terminal event (done/error) is seen — do NOT wait for the
  // HTTP stream to physically close. Through a dev proxy the close can be buffered/
  // delayed, which would otherwise hang the caller's progress bar even though the
  // backend already finished and sent `done`.
  let terminal = false;
  const onEvent = (e: ReelJobEvent) => {
    collected.push(e);
    if (e.phase === 'done' || e.phase === 'scheduled' || e.phase === 'error') terminal = true;
    input.onEvent?.(e);
  };
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    buffer = drainSseBuffer(buffer, onEvent);
    if (terminal) { await reader.cancel().catch(() => {}); break; }
  }
  if (!terminal) buffer = drainSseBuffer(buffer + '\n\n', onEvent);
  return collected;
}

// Parse all complete SSE events (`...\n\n`) sitting in `buffer`, emit them, and
// return the unparsed remainder. Shared by the ReadableStream and XHR consumers.
function drainSseBuffer(buffer: string, onEvent: (event: ReelJobEvent) => void): string {
  let idx: number;
  while ((idx = buffer.indexOf('\n\n')) !== -1) {
    const chunk = buffer.slice(0, idx);
    buffer = buffer.slice(idx + 2);
    const dataLine = chunk.split('\n').find((l) => l.startsWith('data:'));
    if (!dataLine) continue;
    try {
      onEvent(JSON.parse(dataLine.slice(5).trimStart()) as ReelJobEvent);
    } catch {
      // skip malformed
    }
  }
  return buffer;
}

export async function parseSseStream(
  stream: ReadableStream<Uint8Array>,
  onEvent: (event: ReelJobEvent) => void,
): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    buffer = drainSseBuffer(buffer, onEvent);
  }
}

// Uses XMLHttpRequest (not fetch) so we can report upload progress via
// `xhr.upload.onprogress`. The SSE response is parsed incrementally from the
// growing `responseText` in `xhr.onprogress`, preserving real-time streaming.
//
// We run TWO concurrent channels for the same job:
//   1. SSE over the XHR connection — fast, push-based render% updates.
//   2. Polling GET /api/reels/:jobId/status — kicks off as soon as we learn
//      the jobId from the first SSE event ({phase:'job'}). Independent of the
//      XHR's health. Backed by Redis JobStore so a backend pod restart still
//      serves a coherent snapshot.
// First-to-terminal wins, the other is aborted/ignored. `sameEvent` dedupes
// so the caller's `onEvent` never sees the same render%/account twice.
//
// Why both: some mobile carriers / Telegram WebView buffer the SSE response
// stream — the XHR stays "alive" but events arrive bunched at the end. The
// progress bar would freeze even though the backend is making progress.
// Concurrent polling fills the gap. A pre-2xx HTTP failure (xhr.onload with
// status outside 2xx) still rejects with the original status.
export async function submitReelJobServer(input: SubmitReelJobInput): Promise<ReelJobEvent[]> {
  const form = new FormData();
  form.append('meta', JSON.stringify(input.meta));
  form.append('file', input.file, input.file.name);

  return new Promise<ReelJobEvent[]>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/reels');
    xhr.setRequestHeader('Accept', 'text/event-stream');
    // XHR перехват fetch не видит — вход ставим сами.
    const token = backendTokenSync();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    const collected: ReelJobEvent[] = [];
    let parsedLen = 0;
    let buffer = '';
    let jobId: string | null = null;
    let terminal = false;
    let settled = false;
    let pollingStarted = false;
    let pollingAbort: AbortController | null = null;
    // True once the request body has been fully sent (xhr.upload.onload). Until
    // then the server cannot have created the job, so any 404 from polling means
    // "still uploading", and an XHR error means the upload itself failed.
    let uploadComplete = false;
    const startedAt = Date.now();

    function telemetry(settleTag: string, extra: Record<string, unknown> = {}) {
      sendPublishTelemetry({
        site: 'submitReelJobServer',
        settle: settleTag,
        jobId: jobId ?? input.meta.jobId,
        terminal,
        pollingStarted,
        collectedCount: collected.length,
        collectedPhases: collected.map((e) => e.phase),
        xhrStatus: xhr.status,
        elapsedMs: Date.now() - startedAt,
        ...extra,
      });
    }

    function settleResolve(tag: string) {
      if (settled) return;
      settled = true;
      pollingAbort?.abort();
      telemetry(tag);
      resolve(collected);
    }
    function settleReject(tag: string, err: unknown) {
      if (settled) return;
      settled = true;
      pollingAbort?.abort();
      telemetry(tag, { errorName: (err as Error)?.name, errorMessage: (err as Error)?.message });
      reject(err);
    }

    const consume = () => {
      const text = xhr.responseText;
      buffer += text.slice(parsedLen);
      parsedLen = text.length;
      buffer = drainSseBuffer(buffer, (event) => {
        // Dedupe against events the poller may have already delivered.
        if (collected.some((e) => sameEvent(e, event))) return;
        collected.push(event);
        if (event.phase === 'job') {
          jobId = event.jobId;
          input.onJobId?.(event.jobId);
          // Start the concurrent polling channel as soon as we know the jobId.
          startPollingIfNeeded();
        }
        if (event.phase === 'done' || event.phase === 'scheduled' || event.phase === 'error') terminal = true;
        input.onEvent?.(event);
      });
    };

    function startPollingIfNeeded() {
      if (pollingStarted || settled) return;
      const id = jobId ?? input.meta.jobId;
      if (!id) return;
      pollingStarted = true;
      pollingAbort = new AbortController();
      // Chain the caller's signal too so an external abort tears both channels down.
      if (input.signal) {
        if (input.signal.aborted) { pollingAbort.abort(); return; }
        input.signal.addEventListener('abort', () => pollingAbort?.abort(), { once: true });
      }
      telemetry('polling-start');
      pollReelJobStatus(id, {
        onEvent: (event) => {
          if (collected.some((e) => sameEvent(e, event))) return;
          collected.push(event);
          if (event.phase === 'done' || event.phase === 'scheduled' || event.phase === 'error') terminal = true;
          input.onEvent?.(event);
        },
        signal: pollingAbort.signal,
        // Big batches (30+ accounts, each with its own overlay) can run the
        // full pipeline 30-50 minutes — 60 leaves headroom without going to
        // hours, which would mask a real hang.
        timeoutMs: 60 * 60_000,
        // Polling can start before the backend has actually created the job
        // (we kick it off the moment xhr.send fires; the upload might still be
        // in flight). Treat 404 as "not yet" while the upload is in flight, then
        // give the backend a 60s window to register the job after upload completes.
        initialGraceMs: 60_000,
        holdOn404: () => !uploadComplete,
      }).then(
        () => settleResolve('polling-resolved'),
        (err) => {
          // Local abort because the SSE path already settled — ignore.
          if ((err as { name?: string })?.name === 'AbortError') return;
          // If polling gave up (job-lost) but the server was never engaged — no
          // SSE event ever arrived AND the upload never completed — the job was
          // never created. Report it as an upload failure, not a lost job, so the
          // UI tells the editor to retry instead of "publishing in background".
          if (err instanceof JobLostError && collected.length === 0 && !terminal && !uploadComplete) {
            settleReject('upload-failed', new UploadFailedError('upload did not reach server'));
            return;
          }
          settleReject('polling-rejected', err);
        },
      );
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) input.onUploadProgress?.(Math.round((e.loaded / e.total) * 100));
    };
    // Request body fully sent — the server now has the file. From here a 404 from
    // polling means "backend still registering the job", and a connection drop is
    // recoverable in the background (job exists server-side).
    xhr.upload.onload = () => { uploadComplete = true; };
    xhr.onprogress = () => { if (!settled) consume(); };

    // The upload connection died before the body finished sending and before any
    // SSE event arrived: the server never got the file, the job can't exist.
    // Fail fast with UploadFailedError instead of polling a job that will 404
    // forever and then masquerade as "publishing in background".
    function failedBeforeServerEngaged(): boolean {
      return !uploadComplete && collected.length === 0 && !terminal;
    }

    xhr.onload = () => {
      if (settled) { telemetry('onload-after-settle'); return; }
      consume();
      if (xhr.status >= 200 && xhr.status < 300) {
        if (terminal) { settleResolve('onload-2xx-terminal'); return; }
        // SSE finished without a terminal event (some intermediary closed the
        // response cleanly mid-job). Let the polling channel finish.
        telemetry('onload-2xx-no-terminal');
        startPollingIfNeeded();
        return;
      }
      const err = new Error(`Server reels endpoint returned ${xhr.status}`);
      (err as { status?: number }).status = xhr.status;
      settleReject('onload-non-2xx', err);
    };
    xhr.onerror = () => {
      if (settled) return;
      if (terminal) { settleResolve('onerror-terminal'); return; }
      if (failedBeforeServerEngaged()) {
        settleReject('upload-failed', new UploadFailedError('upload connection failed before server received the file'));
        return;
      }
      telemetry('onerror-fallback');
      startPollingIfNeeded();
    };
    xhr.onabort = () => {
      if (settled) return;
      if (input.signal?.aborted) {
        const err: Error & { name: string } = new Error('Aborted');
        err.name = 'AbortError';
        settleReject('onabort-signal', err);
        return;
      }
      if (failedBeforeServerEngaged()) {
        settleReject('upload-failed', new UploadFailedError('upload aborted before server received the file'));
        return;
      }
      telemetry('onabort-fallback');
      startPollingIfNeeded();
    };

    if (input.signal) {
      if (input.signal.aborted) { xhr.abort(); return; }
      input.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    // Authorization уже поставлен сразу после open (backendTokenSync). Второй
    // setRequestHeader у XHR не заменяет, а склеивает: «Bearer X, Bearer X» —
    // и бэкенд отвечал 401 на каждый рилс с сайта (17–18.10).
    xhr.send(form);
    // Kick off polling immediately using meta.jobId. We don't wait for the
    // first SSE event (`phase:'job'`) because some mobile networks / Telegram
    // WebView buffer the entire SSE response — that event would never arrive
    // and polling would never start. 404 in the first 60s is treated as
    // "backend hasn't created the job yet" (see initialGraceMs above).
    startPollingIfNeeded();
  });
}

// Проверка перед загрузкой: тот же ролик в те же аккаунты уже публикуется или
// только что вышел — вернёт jobId той задачи, и форма покажет её ход вместо
// второй публикации (22.10.2026: повторное «Опубликовать» давало дубли в ленте).
// Ошибка проверки (старый бэкенд, сеть) публикацию не останавливает — null.
export async function claimReelJob(input: {
  jobId: string;
  accounts: { id: string }[];
  captions: Record<string, string>;
  fileSize: number;
  scheduledAt?: string;
}): Promise<string | null> {
  try {
    const res = await backendFetch('/api/reels/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (res.status !== 409) return null;
    const body = (await res.json()) as { duplicateOf?: unknown };
    return typeof body.duplicateOf === 'string' ? body.duplicateOf : null;
  } catch {
    return null;
  }
}

// Explicit user-cancel — tells the backend to kill ffmpeg + skip publish.
// Fire-and-forget from the caller's perspective; backend returns 200 once
// the terminal marker is in the JobStore, 404 if the job is unknown, 409 if
// already terminal. Any error here is logged-and-ignored — the local UI
// already proceeded to "cancelled" state.
export async function cancelReelJob(jobId: string): Promise<void> {
  try {
    await backendFetch(`/api/reels/${encodeURIComponent(jobId)}/cancel`, { method: 'POST' });
  } catch {
    // best-effort
  }
}

function sameEvent(a: ReelJobEvent, b: ReelJobEvent): boolean {
  if (a.phase !== b.phase) return false;
  if (a.phase === 'render' && b.phase === 'render') {
    // Per-task render dedupe: identical percent on *different* tasks is a
    // distinct progress event, not a duplicate.
    return a.percent === b.percent && a.taskIdx === b.taskIdx;
  }
  if (a.phase === 'publish' && b.phase === 'publish') {
    return a.accountId === b.accountId && a.status === b.status;
  }
  if (a.phase === 'done' && b.phase === 'done') return true;
  if (a.phase === 'error' && b.phase === 'error') return a.error === b.error;
  return false;
}

export function isServerUnavailable(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (err instanceof Error && 'status' in err) {
    const status = (err as { status?: number }).status;
    if (status === 0 || status === 502 || status === 503 || status === 504) return true;
  }
  return false;
}

// Snapshot shape returned by GET /api/reels/:jobId/status (mirrors backend JobState).
export interface JobStatusSnapshot {
  jobId: string;
  batchId: string;
  phase: 'uploading' | 'render' | 'publish' | 'done' | 'error';
  percent: number;
  taskIdx?: number;
  totalTasks?: number;
  accounts: Record<string, { status: 'pending' | 'retrying' | 'success' | 'error'; instagramMediaId?: string; error?: string }>;
  done: boolean;
  error?: string;
  createdAt: number;
  updatedAt: number;
  expiresAt: number;
}

export interface PollReelJobStatusOptions {
  onEvent: (event: ReelJobEvent) => void;
  intervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
  // 404 inside this window means "backend hasn't created the job yet" rather
  // than "job is lost" — keep polling. Lets callers fire polling concurrently
  // with the POST that creates the job (upload may still be in flight).
  initialGraceMs?: number;
  // Dynamic grace: while this returns true, a 404 is ALWAYS treated as "not yet"
  // (job not created), independent of initialGraceMs. submitReelJobServer passes
  // `() => !uploadComplete` so a slow upload never trips a premature 'job-lost'.
  holdOn404?: () => boolean;
}

export class JobLostError extends Error {
  readonly code = 'job-lost' as const;
  constructor(message: string) {
    super(message);
    this.name = 'JobLostError';
  }
}

// The multipart upload never reached the server: the POST errored / aborted
// before the request body finished sending, AND no SSE event or status snapshot
// was ever observed. The job therefore never existed — unlike JobLostError this
// is NOT recoverable in the background; the editor must retry the upload.
export class UploadFailedError extends Error {
  readonly code = 'upload-failed' as const;
  constructor(message: string) {
    super(message);
    this.name = 'UploadFailedError';
  }
}

// Polls GET /api/reels/:jobId/status until the job reaches a terminal state
// (done or error), emitting a synthetic ReelJobEvent for each *change* seen
// between snapshots. Resolves with the collected events. Rejects with
// JobLostError on 404 / timeout, AbortError on signal.
export function pollReelJobStatus(
  jobId: string,
  opts: PollReelJobStatusOptions,
): Promise<ReelJobEvent[]> {
  const intervalMs = opts.intervalMs ?? 3000;
  // Retry paths (5xx, network blips) must not spin: clamp their delay to 250ms
  // even if the caller passes a smaller intervalMs. The success path keeps the
  // caller's intervalMs verbatim since each iteration does real network work.
  const retryDelayMs = Math.max(intervalMs, 250);
  const timeoutMs = opts.timeoutMs ?? 300_000;
  const initialGraceMs = opts.initialGraceMs ?? 0;
  const collected: ReelJobEvent[] = [];
  const startedAt = Date.now();
  let prev: JobStatusSnapshot | null = null;

  const promise = new Promise<ReelJobEvent[]>((resolve, reject) => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function cleanup() {
      stopped = true;
      if (timer) { clearTimeout(timer); timer = null; }
      if (opts.signal) opts.signal.removeEventListener('abort', onAbort);
    }

    function onAbort() {
      cleanup();
      const err: Error & { name: string } = new Error('Aborted');
      err.name = 'AbortError';
      reject(err);
    }

    if (opts.signal) {
      if (opts.signal.aborted) { onAbort(); return; }
      opts.signal.addEventListener('abort', onAbort, { once: true });
    }

    async function tick() {
      if (stopped) return;
      if (Date.now() - startedAt > timeoutMs) {
        cleanup();
        reject(new JobLostError('polling timed out'));
        return;
      }
      let res: Response;
      try {
        res = await backendFetch(`/api/reels/${encodeURIComponent(jobId)}/status`, { signal: opts.signal });
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') { cleanup(); reject(err); return; }
        // Network blip — keep polling until timeout. Guard against scheduling a
        // stray timer if abort/cleanup happened while the fetch was in flight.
        if (stopped) return;
        timer = setTimeout(tick, retryDelayMs);
        return;
      }
      if (res.status === 404) {
        // 404 = backend hasn't created the job yet. Keep polling while EITHER:
        //  - the caller's dynamic hold says so (e.g. upload still in flight), or
        //  - we're inside the static initial grace window.
        // Only once both say "give up" do we treat it as a genuinely lost job.
        const held = opts.holdOn404?.() ?? false;
        if (held || Date.now() - startedAt < initialGraceMs) {
          if (stopped) return;
          timer = setTimeout(tick, retryDelayMs);
          return;
        }
        cleanup();
        reject(new JobLostError('job not found'));
        return;
      }
      if (!res.ok) {
        // Treat 5xx as transient and keep going.
        if (stopped) return;
        timer = setTimeout(tick, retryDelayMs);
        return;
      }
      const snap = (await res.json()) as JobStatusSnapshot;
      const newEvents = diffSnapshotToEvents(prev, snap);
      // Diagnostic: log each tick to console + telemetry so we can see what
      // polling actually receives in Telegram WebView without devtools.
      try {
        // eslint-disable-next-line no-console
        console.log('[poll]', { jobId, phase: snap.phase, percent: snap.percent, done: snap.done, accounts: Object.fromEntries(Object.entries(snap.accounts).map(([k, a]) => [k, a.status])), newEvents: newEvents.map(e => e.phase) });
        sendPublishTelemetry({
          site: 'pollReelJobStatus',
          settle: 'tick',
          jobId,
          phase: snap.phase,
          percent: snap.percent,
          done: snap.done,
          accounts: Object.fromEntries(Object.entries(snap.accounts).map(([k, a]) => [k, a.status])),
          newEventCount: newEvents.length,
          newEventPhases: newEvents.map(e => e.phase),
        });
      } catch { /* best effort */ }
      for (const ev of newEvents) {
        collected.push(ev);
        opts.onEvent(ev);
      }
      prev = snap;
      // Resolve only when the snapshot is *coherently* terminal: done:true AND
      // every account has reached a terminal status. Without the second check
      // we'd resolve on an inconsistent snapshot (seen in prod) and report a
      // false 'Публикация не удалась' for a publish that ultimately succeeds.
      const accountsAllTerminal = Object.values(snap.accounts).every(
        (a) => a.status === 'success' || a.status === 'error',
      );
      if (snap.done && (accountsAllTerminal || snap.phase === 'error')) {
        cleanup();
        resolve(collected);
        return;
      }
      if (stopped) return;
      timer = setTimeout(tick, intervalMs);
    }

    // Kick off immediately; intervalMs gates only subsequent polls.
    tick();
  });
  // Attach a no-op catch synchronously so a rejection that fires before the
  // caller awaits doesn't trip Node's unhandledRejection tracker (relevant
  // under vitest fake timers where the microtask draining out-paces the
  // caller's `await`).
  promise.catch(() => {});
  return promise;
}

// Compare a fresh snapshot to the previous one and return the ReelJobEvents
// that would have been emitted by the SSE path. Skips duplicates.
function diffSnapshotToEvents(prev: JobStatusSnapshot | null, next: JobStatusSnapshot): ReelJobEvent[] {
  const out: ReelJobEvent[] = [];
  // Phase/percent change → render event (the bar uses render percent).
  if (next.phase === 'render') {
    if (!prev || prev.phase !== 'render' || prev.percent !== next.percent) {
      out.push({ phase: 'render', percent: next.percent, taskIdx: next.taskIdx, totalTasks: next.totalTasks });
    }
  }
  // Phase transition into 'publish' → emit a publish-start pulse (no accountId)
  // so the UI flips the subtitle from 'Сервер обрабатывает...' to 'Публикуем в
  // Instagram' as soon as the backend hits the IG container step, instead of
  // waiting up to ~2 minutes for the first per-account success event.
  if (next.phase === 'publish' && prev?.phase !== 'publish') {
    out.push({ phase: 'publish', percent: 0 });
  }
  // Per-account publish transitions.
  for (const [id, acct] of Object.entries(next.accounts)) {
    const prevAcct = prev?.accounts[id];
    if (acct.status === 'success' && prevAcct?.status !== 'success') {
      out.push({ phase: 'publish', accountId: id, status: 'success' });
    } else if (acct.status === 'error' && prevAcct?.status !== 'error') {
      out.push({ phase: 'publish', accountId: id, status: 'error', error: acct.error });
    } else if (acct.status === 'retrying' && prevAcct?.status !== 'retrying') {
      // Non-terminal: an IG-side transient failure is being auto-retried. The
      // terminal check below only matches success/error, so polling continues.
      out.push({ phase: 'publish', accountId: id, status: 'retrying', error: acct.error });
    }
  }
  // Terminal done → emit `done` event with results derived from accounts.
  // Safety guard: if the snapshot reports done:true but any account is still
  // `pending`, the store is in an inconsistent state (seen in prod against an
  // iPhone Safari WebView job; root cause TBD). Don't emit a synthetic `done`
  // with pending-status results — that gets mapped to `error: 'Публикация не
  // удалась'` even when the actual publish later succeeds. Continue polling.
  // The transition is "first time the snapshot is coherently terminal", so we
  // compare against whether `prev` was *also* coherently terminal — not just
  // `prev.done` — otherwise an earlier inconsistent done:true would suppress
  // the legitimate later emission.
  const allAccountsTerminal = Object.values(next.accounts).every(
    (a) => a.status === 'success' || a.status === 'error',
  );
  const prevAllAccountsTerminal = prev
    ? Object.values(prev.accounts).every((a) => a.status === 'success' || a.status === 'error')
    : false;
  const prevCoherentDone = !!prev?.done && prevAllAccountsTerminal;
  if (next.done && allAccountsTerminal && !prevCoherentDone) {
    if (next.phase === 'error') {
      out.push({ phase: 'error', error: next.error || 'unknown error' });
    } else {
      const results = Object.entries(next.accounts).map(([accountId, a]) => ({
        accountId,
        status: a.status as 'success' | 'error',
        instagramMediaId: a.instagramMediaId,
        error: a.error,
      }));
      out.push({ phase: 'done', results });
    }
  }
  return out;
}

import { api } from './client';
import type { PublicationCategory } from '../types/common';

export interface ReelMedia {
  url: string;
}

export interface Reel {
  id: string;
  accountId: string;
  media: ReelMedia;
  caption: string;
  status: 'draft' | 'published' | 'failed';
  instagramMediaId?: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  category?: PublicationCategory;
}

export interface CreateReelRequest {
  accountId: string;
  media: ReelMedia;
  caption?: string;
  batchId?: string;
  category?: PublicationCategory;
  collaborators?: string[];
}

export interface CreateReelResponse {
  reel: Reel;
}

export interface GetReelsResponse {
  reels: Reel[];
}

export interface GetReelResponse {
  reel: Reel;
}

export interface PublishReelResponse {
  reel: Reel;
}

export const reelsApi = {
  /**
   * Получить все рилсы
   */
  getAll: () => api.get<GetReelsResponse>('/reels'),
  
  /**
   * Получить рилс по ID
   */
  getById: (id: string) => api.get<GetReelResponse>(`/reels/${id}`),
  
  /**
   * Создать новый рилс
   */
  create: (data: CreateReelRequest) => 
    api.post<CreateReelResponse>('/reels', data),
  
  /**
   * Обновить рилс
   */
  update: (id: string, data: Partial<Reel>) =>
    api.put<GetReelResponse>(`/reels/${id}`, data),
  
  /**
   * Удалить рилс
   */
  delete: (id: string) => api.delete<{ success: boolean }>(`/reels/${id}`),
  
  /**
   * Опубликовать рилс в Instagram
   */
  publish: (id: string, options?: { duplicateToStory?: boolean }) =>
    api.post<PublishReelResponse>(`/reels/${id}/publish`, options),
};